<template>
  <div
    class="min-h-screen bg-background p-6 pb-24 pt-safe"
    @touchstart.passive="onPullStart"
    @touchmove.passive="onPullMove"
    @touchend.passive="onPullEnd"
  >
    <!-- Pull-to-refresh indicator -->
    <div
      class="flex items-center justify-center overflow-hidden"
      :class="{ 'transition-all duration-300': !isPulling }"
      :style="{ height: `${pullIndicatorHeight}px` }"
    >
      <div v-if="isRefreshing" class="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      <span v-else-if="isPulling && pullDistance > 0" class="text-xs text-textMuted">
        {{ pullDistance >= PULL_THRESHOLD ? '放開刷新' : '下拉刷新' }}
      </span>
    </div>

    <h2 class="text-2xl font-bold text-textMain mb-8 mt-12">設定</h2>

    <!-- 寶寶管理 -->
    <div class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">寶寶管理</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div
          v-for="(baby, index) in appStore.babies"
          :key="baby.id"
          class="p-4 flex items-center justify-between"
          :class="{ 'border-b border-gray-50': index < appStore.babies.length - 1 }"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 border-gray-100"
              :class="baby.gender === 'boy' ? 'bg-blue-100' : 'bg-pink-100'"
            >
              {{ baby.gender === 'boy' ? '👦' : '👧' }}
            </div>
            <div>
              <p class="font-bold text-textMain text-sm">{{ baby.nickname }}</p>
              <p class="text-xs text-textMuted">生日：{{ baby.birthDate.replace(/-/g, '/') }}</p>
            </div>
          </div>
          <button
            @click="showDeleteBabyDialog(baby)"
            class="text-red-400 text-sm font-medium px-3 py-1 rounded-lg hover:bg-red-50 active:scale-95 transition-all"
          >
            刪除
          </button>
        </div>
      </div>
    </div>

    <!-- 帳號管理 -->
    <div v-if="authStore.isAnonymous" class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">帳號管理</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div class="p-4 border-b border-gray-50">
          <div class="flex items-start gap-3 mb-3">
            <div class="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
              💾
            </div>
            <div class="flex-1">
              <p class="text-sm font-bold text-textMain mb-1">建立帳號備份資料</p>
              <p class="text-xs text-textMuted leading-relaxed">
                建立新帳號後可在其他裝置登入並同步資料。
              </p>
            </div>
          </div>
          <button
            @click="showCreateAccountDialog"
            class="w-full bg-primary text-white font-bold py-3 rounded-xl active:scale-95 transition-all"
          >
            建立帳號
          </button>
        </div>
        <div class="p-4">
          <div class="flex items-start gap-3 mb-3">
            <div class="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              🔑
            </div>
            <div class="flex-1">
              <p class="text-sm font-bold text-textMain mb-1">登入現有帳號</p>
              <p class="text-xs text-textMuted leading-relaxed">
                已有帳號？登入以同步您的資料。
              </p>
            </div>
          </div>
          <button
            @click="showLoginDialog"
            class="w-full bg-white text-primary font-bold py-3 rounded-xl border-2 border-primary active:scale-95 transition-all"
          >
            登入帳號
          </button>
        </div>
      </div>
    </div>

    <!-- 家庭共享 -->
    <div class="mb-6" v-if="!authStore.isAnonymous">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">家庭共享</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">

        <!-- 匿名帳號提示（不應出現，已有 v-if 擋在外層，保留供型別安全） -->

        <!-- Guest 視圖：已連結到家庭 -->
        <div v-if="authStore.linkedTo">
          <div class="p-4 border-b border-gray-50">
            <div class="flex items-start gap-3">
              <div class="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">🔗</div>
              <div class="flex-1">
                <p class="text-sm font-bold text-textMain mb-1">
                  已加入{{ authStore.ownerFamilyName ? `「${authStore.ownerFamilyName}」` : '家庭' }}
                </p>
                <p class="text-xs text-textMuted leading-relaxed">
                  家庭代碼：<span class="font-mono font-bold tracking-widest text-textMain">{{ authStore.linkedTo.familyCode }}</span>
                </p>
                <p class="text-xs text-textMuted mt-1">目前查看的是家庭共享資料</p>
              </div>
            </div>
          </div>
          <div class="p-4">
            <button
              @click="showLeaveFamilyConfirm = true"
              :disabled="familyActionLoading"
              class="w-full text-red-400 font-bold py-2 rounded-xl border border-red-100 active:scale-95 transition-all text-sm disabled:opacity-40"
            >
              離開家庭
            </button>
          </div>
        </div>

        <!-- Owner 視圖：有家庭代碼 -->
        <div v-else-if="authStore.familyCode">
          <!-- 家庭名稱 -->
          <div class="p-4 border-b border-gray-50">
            <p class="text-xs text-textMuted mb-2">家庭名稱</p>
            <div v-if="editingFamilyName" class="flex gap-2">
              <input
                v-model="editFamilyNameValue"
                maxlength="20"
                placeholder="例如：我們的家"
                class="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary"
                @keyup.enter="handleUpdateFamilyName"
              />
              <button @click="handleUpdateFamilyName" class="px-3 py-2 bg-primary text-white text-xs font-bold rounded-xl active:scale-95">儲存</button>
              <button @click="editingFamilyName = false" class="px-3 py-2 border border-gray-200 text-xs text-textMuted rounded-xl active:scale-95">取消</button>
            </div>
            <div v-else class="flex items-center gap-3">
              <span class="flex-1 text-sm font-bold text-textMain">{{ authStore.familyName || '（未設定）' }}</span>
              <button @click="startEditFamilyName" class="text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-lg font-bold active:scale-95 transition-all">編輯</button>
            </div>
          </div>
          <!-- 代碼顯示 -->
          <div class="p-4 border-b border-gray-50">
            <p class="text-xs text-textMuted mb-2">我的家庭代碼（讓家人輸入此代碼加入）</p>
            <div class="flex items-center gap-3">
              <span class="font-mono font-bold text-2xl tracking-widest text-primary flex-1">{{ authStore.familyCode }}</span>
              <button
                @click="copyFamilyCode"
                class="text-xs px-3 py-1.5 bg-primary/10 text-primary rounded-lg font-bold active:scale-95 transition-all"
              >
                複製
              </button>
            </div>
          </div>

          <!-- 成員列表 -->
          <div v-if="familyMembers.length > 0">
            <div class="px-4 pt-3 pb-1">
              <p class="text-xs font-bold text-textMuted">家庭成員</p>
            </div>
            <div
              v-for="(member, memberUid) in authStore.sharedWith"
              :key="memberUid"
              class="px-4 py-3 flex items-center justify-between border-b border-gray-50 last:border-b-0"
            >
              <div>
                <p class="text-sm font-bold text-textMain">{{ member.nickname }}</p>
                <p v-if="member.email" class="text-xs text-textMuted">{{ member.email }}</p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="openEditNickname(memberUid as string, member.nickname)"
                  class="text-xs text-primary font-bold px-2 py-1 rounded active:scale-95"
                >
                  改名
                </button>
                <button
                  @click="showRevokeConfirm(memberUid as string, member.nickname)"
                  class="text-xs text-red-400 font-bold px-2 py-1 rounded active:scale-95"
                >
                  移除
                </button>
              </div>
            </div>
          </div>

          <!-- 待審核申請 -->
          <div v-if="pendingMembersList.length > 0">
            <div class="px-4 pt-3 pb-1">
              <p class="text-xs font-bold text-orange-500">待審核申請（{{ pendingMembersList.length }}）</p>
            </div>
            <div
              v-for="([memberUid, pending]) in pendingMembersList"
              :key="memberUid"
              class="px-4 py-3 flex items-center justify-between border-b border-gray-50"
            >
              <div>
                <p class="text-sm font-bold text-textMain">{{ pending.email || '匿名用戶' }}</p>
                <p class="text-xs text-textMuted">申請時間：{{ new Date(pending.requestedAt).toLocaleDateString('zh-TW') }}</p>
              </div>
              <div class="flex items-center gap-2">
                <button
                  @click="handleApproveMember(memberUid)"
                  :disabled="familyActionLoading"
                  class="text-xs text-green-600 font-bold px-3 py-1.5 rounded-lg bg-green-50 active:scale-95 disabled:opacity-40"
                >
                  同意
                </button>
                <button
                  @click="handleRejectMember(memberUid)"
                  :disabled="familyActionLoading"
                  class="text-xs text-red-400 font-bold px-3 py-1.5 rounded-lg bg-red-50 active:scale-95 disabled:opacity-40"
                >
                  拒絕
                </button>
              </div>
            </div>
          </div>

          <!-- 重新產生代碼 / 解除家庭共享 -->
          <div class="p-4 flex gap-2">
            <button
              @click="handleRegenerateCode"
              :disabled="familyActionLoading"
              class="flex-1 text-textMuted text-xs py-2 rounded-xl border border-gray-200 active:scale-95 transition-all disabled:opacity-40"
            >
              重新產生代碼
            </button>
            <button
              @click="showDeleteFamilyCodeConfirm = true"
              :disabled="familyActionLoading"
              class="flex-1 text-red-400 text-xs py-2 rounded-xl border border-red-100 active:scale-95 transition-all disabled:opacity-40"
            >
              解除家庭共享
            </button>
          </div>
        </div>

        <!-- 未加入視圖：申請中 or 提供加入/建立選項 -->
        <div v-else>

          <!-- 申請中：等待 owner 審核 -->
          <div v-if="authStore.pendingWith">
            <div class="p-4 border-b border-gray-50">
              <div class="flex items-start gap-3">
                <div class="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">⏳</div>
                <div class="flex-1">
                  <p class="text-sm font-bold text-textMain mb-1">申請中，等待審核</p>
                  <p class="text-xs text-textMuted leading-relaxed">
                    家庭代碼：<span class="font-mono font-bold tracking-widest text-textMain">{{ authStore.pendingWith.familyCode }}</span>
                  </p>
                  <p class="text-xs text-textMuted mt-1">對方同意後即可查看共享資料</p>
                </div>
              </div>
            </div>
            <div class="p-4">
              <button
                @click="showCancelRequestConfirm = true"
                :disabled="familyActionLoading"
                class="w-full text-red-400 font-bold py-2 rounded-xl border border-red-100 active:scale-95 transition-all text-sm disabled:opacity-40"
              >
                取消申請
              </button>
            </div>
          </div>

          <!-- 一般狀態：加入 or 建立 -->
          <template v-else>
            <!-- 加入家庭 -->
            <div class="p-4 border-b border-gray-50">
              <p class="text-xs font-bold text-textMuted mb-2">輸入家庭代碼加入</p>
              <div class="flex gap-2">
                <input
                  v-model="joinCodeInput"
                  maxlength="6"
                  placeholder="例如 ABC123"
                  class="flex-1 border border-gray-200 rounded-xl px-3 py-2 text-sm font-mono uppercase tracking-widest focus:outline-none focus:border-primary"
                  @input="joinCodeInput = joinCodeInput.toUpperCase()"
                />
                <button
                  @click="handleJoinFamily"
                  :disabled="joinCodeInput.length < 6 || familyActionLoading"
                  class="px-4 py-2 bg-primary text-white text-sm font-bold rounded-xl active:scale-95 transition-all disabled:opacity-40 whitespace-nowrap shrink-0"
                >
                  {{ familyActionLoading ? '送出中...' : '申請加入' }}
                </button>
              </div>
              <p v-if="familyError" class="text-xs text-red-400 mt-1.5">{{ familyError }}</p>
            </div>

            <!-- 建立家庭代碼 -->
            <div class="p-4">
              <p class="text-xs text-textMuted mb-2">讓家人加入你的資料</p>
              <input
                v-model="newFamilyName"
                maxlength="20"
                placeholder="家庭名稱（選填，例如：我們的家）"
                class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary mb-3"
              />
              <button
                @click="handleCreateFamilyCode"
                :disabled="familyActionLoading"
                class="w-full bg-white text-primary font-bold py-2.5 rounded-xl border-2 border-primary active:scale-95 transition-all text-sm disabled:opacity-40"
              >
                建立我的家庭代碼
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>

    <!-- 推播提醒 -->
    <div class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">推播提醒</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <!-- 展開/收起提醒設定 -->
        <div class="p-4 flex justify-between items-center" :class="{ 'border-b border-gray-50': showTimingOptions }">
          <div class="flex-1">
            <p class="text-sm font-bold text-textMain mb-1">疫苗提醒通知</p>
            <p class="text-xs text-textMuted">點擊設定提醒時間</p>
          </div>
          <button
            @click="toggleNotifications"
            class="w-12 h-7 rounded-full transition-colors relative"
            :class="notificationStore.isEnabled ? 'bg-primary' : 'bg-gray-300'"
          >
            <div
              class="w-5 h-5 bg-white rounded-full absolute top-1 transition-transform"
              :class="notificationStore.isEnabled ? 'translate-x-6' : 'translate-x-1'"
            ></div>
          </button>
        </div>

        <!-- 提醒時間點設定 -->
        <div v-if="showTimingOptions" class="p-4 border-b border-gray-50">
          <p class="text-xs font-bold text-textMuted mb-3">提醒時間點</p>
          <div class="space-y-3">
            <label
              v-for="timing in TIMING_OPTIONS"
              :key="timing.value"
              class="flex items-center gap-3 cursor-pointer"
            >
              <input
                type="checkbox"
                :value="timing.value"
                v-model="selectedTimings"
                @change="updateTimings"
                class="w-5 h-5 rounded border-2 border-gray-300 text-primary focus:ring-2 focus:ring-primary/20"
              />
              <div class="flex-1">
                <p class="text-sm font-bold text-textMain">{{ timing.label }}</p>
                <p class="text-xs text-textMuted">{{ timing.description }}</p>
              </div>
            </label>
          </div>
        </div>

      </div>
    </div>

    <!-- 行事曆偏好（iOS 顯示兩個選項，Android 只顯示 Google） -->
    <div v-if="isIOS || isAndroid" class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">行事曆</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <p v-if="calendarPreference === null" class="px-4 pt-3 text-xs text-textMuted">
          尚未設定，點擊任一疫苗行事曆按鈕時會詢問偏好
        </p>
        <div :class="{ 'border-t border-gray-50': calendarPreference === null }">
          <div
            class="p-4 flex items-center justify-between border-b border-gray-50 cursor-pointer active:bg-gray-50"
            @click="setCalendarPreference('google')"
          >
            <div class="flex items-center gap-3">
              <span class="text-lg">🗓️</span>
              <span class="text-sm font-bold text-textMain">Google 行事曆</span>
            </div>
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              :class="calendarPreference === 'google' ? 'border-primary bg-primary' : 'border-gray-300'"
            >
              <div v-if="calendarPreference === 'google'" class="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
          <div
            v-if="isIOS"
            class="p-4 flex items-center justify-between cursor-pointer active:bg-gray-50"
            @click="setCalendarPreference('apple')"
          >
            <div class="flex items-center gap-3">
              <span class="text-lg">📅</span>
              <span class="text-sm font-bold text-textMain">Apple 行事曆</span>
            </div>
            <div
              class="w-5 h-5 rounded-full border-2 flex items-center justify-center"
              :class="calendarPreference === 'apple' ? 'border-primary bg-primary' : 'border-gray-300'"
            >
              <div v-if="calendarPreference === 'apple'" class="w-2 h-2 bg-white rounded-full"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 系統資訊 -->
    <div class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">系統資訊</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <div class="p-4 border-b border-gray-50 flex justify-between items-center">
          <span class="text-sm font-bold text-textMain">帳號類型</span>
          <span class="text-xs px-2 py-1 rounded" :class="authStore.isAnonymous ? 'bg-gray-100 text-gray-600' : 'bg-green-50 text-green-600'">
            {{ authStore.isAnonymous ? '匿名' : '已綁定信箱' }}
          </span>
        </div>
        <div class="p-4 border-b border-gray-50 flex justify-between items-center">
          <span class="text-sm font-bold text-textMain">資料儲存位置</span>
          <span class="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded">雲端</span>
        </div>
        <div class="p-4 flex justify-between items-center">
          <span class="text-sm font-bold text-textMain">應用程式版本</span>
          <span class="text-xs text-textMuted">{{ appVersion }}</span>
        </div>
      </div>
    </div>

    <!-- 法律資訊 -->
    <div class="mb-6">
      <h3 class="text-sm font-bold text-textMain mb-3 px-1">法律資訊</h3>
      <div class="bg-white rounded-2xl shadow-sm overflow-hidden">
        <router-link
          to="/privacy"
          class="p-4 border-b border-gray-50 flex justify-between items-center"
        >
          <div class="flex items-center gap-3">
            <span class="text-lg">📜</span>
            <span class="text-sm font-bold text-textMain">隱私政策</span>
          </div>
          <span class="text-textMuted">›</span>
        </router-link>
        <router-link
          to="/terms"
          class="p-4 flex justify-between items-center"
        >
          <div class="flex items-center gap-3">
            <span class="text-lg">📋</span>
            <span class="text-sm font-bold text-textMain">使用條款</span>
          </div>
          <span class="text-textMuted">›</span>
        </router-link>
      </div>
    </div>

    <!-- 危險操作 -->
    <div class="mb-6">
      <h3 class="text-sm font-bold text-red-400 mb-3 px-1">危險操作</h3>
      <button
        @click="showDeleteAccountDialog"
        class="w-full bg-white text-red-400 font-bold py-4 rounded-2xl shadow-sm border border-red-100 active:scale-95 transition-all"
      >
        刪除帳號
      </button>
    </div>

    <!-- 取消申請確認對話框 -->
    <ConfirmDialog
      :show="showCancelRequestConfirm"
      title="取消申請"
      message="確定要取消加入申請嗎？"
      confirmText="取消申請"
      danger
      @confirm="handleCancelJoinRequest"
      @cancel="showCancelRequestConfirm = false"
    />

    <!-- 離開家庭確認對話框 -->
    <ConfirmDialog
      :show="showLeaveFamilyConfirm"
      title="離開家庭"
      message="確定要離開家庭嗎？&#10;&#10;離開後將只能看到自己的資料。"
      confirmText="離開"
      danger
      @confirm="handleLeaveFamily"
      @cancel="showLeaveFamilyConfirm = false"
    />

    <!-- 移除成員確認對話框 -->
    <ConfirmDialog
      :show="revokeDialog.show"
      title="移除成員"
      :message="`確定要移除「${revokeDialog.nickname}」嗎？\n\n移除後對方將無法繼續查看共享資料。`"
      confirmText="移除"
      danger
      @confirm="handleRevokeAccess"
      @cancel="revokeDialog.show = false"
    />

    <!-- 重新產生代碼確認對話框 -->
    <ConfirmDialog
      :show="regenerateConfirmDialog"
      title="重新產生代碼"
      message="目前有家庭成員，重新產生代碼不會移除現有成員，確定要繼續嗎？"
      confirmText="確定產生"
      @confirm="doRegenerateCode"
      @cancel="regenerateConfirmDialog = false"
    />

    <!-- 解除家庭共享確認對話框 -->
    <ConfirmDialog
      :show="showDeleteFamilyCodeConfirm"
      title="解除家庭共享"
      :message="familyMembers.length > 0 ? '解除後所有家庭成員將無法繼續查看共享資料，確定要解除嗎？' : '解除後您可以加入其他人的家庭，確定要解除嗎？'"
      confirmText="解除"
      danger
      @confirm="handleDeleteFamilyCode"
      @cancel="showDeleteFamilyCodeConfirm = false"
    />

    <!-- 修改成員名稱對話框（簡易 inline） -->
    <div
      v-if="editNicknameDialog.show"
      class="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-6"
      @click.self="editNicknameDialog.show = false"
    >
      <div class="bg-white rounded-2xl p-6 w-full max-w-sm shadow-xl" @click.stop>
        <h3 class="font-bold text-textMain mb-4">修改成員名稱</h3>
        <input
          v-model="editNicknameDialog.value"
          class="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-primary mb-4"
          placeholder="例如：老公、媽媽"
          maxlength="10"
        />
        <div class="flex gap-2">
          <button
            @click="editNicknameDialog.show = false"
            class="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm text-textMuted"
          >
            取消
          </button>
          <button
            @click="handleUpdateNickname"
            :disabled="!editNicknameDialog.value.trim()"
            class="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-bold disabled:opacity-40"
          >
            確定
          </button>
        </div>
      </div>
    </div>

    <!-- 刪除寶寶確認對話框 -->
    <ConfirmDialog
      :show="deleteBabyDialog.show"
      :title="deleteBabyDialog.title"
      :message="deleteBabyDialog.message"
      danger
      @confirm="handleDeleteBaby"
      @cancel="closeBabyDialog"
    />

    <!-- 刪除帳號確認對話框 -->
    <ConfirmDialog
      :show="deleteAccountDialog.show"
      :loading="deleteAccountDialog.loading"
      title="刪除帳號"
      message="確定要刪除帳號嗎？&#10;&#10;此動作將永久刪除：&#10;• 您的帳號&#10;• 所有寶寶資料&#10;• 所有疫苗紀錄&#10;&#10;刪除後將無法復原，並會自動登出。"
      confirmText="刪除"
      loadingText="刪除中..."
      danger
      @confirm="handleDeleteAccount"
      @cancel="closeDeleteAccountDialog"
    />

    <!-- 重新驗證對話框 -->
    <ReauthenticateDialog
      :show="reauthenticateDialog.show"
      ref="reauthenticateDialogRef"
      @confirm="handleReauthenticate"
      @cancel="closeReauthenticateDialog"
    />

    <!-- 建立帳號對話框 -->
    <CreateAccountDialog
      :show="createAccountDialog.show"
      ref="createAccountDialogRef"
      @confirm="handleCreateAccount"
      @cancel="closeCreateAccountDialog"
    />

    <!-- 登入對話框 -->
    <LoginDialog
      :show="loginDialog.show"
      ref="loginDialogRef"
      @confirm="handleLogin"
      @cancel="closeLoginDialog"
    />

    <!-- 通知引導對話框 -->
    <NotificationGuideDialog
      :show="notificationGuideDialog.show"
      @confirm="handleNotificationGuideConfirm"
      @cancel="handleNotificationGuideCancel"
    />

    <!-- 成功/錯誤提示 -->
    <Transition name="toast">
      <div
        v-if="toast.show"
        class="fixed bottom-28 left-1/2 -translate-x-1/2 bg-textMain text-white px-6 py-3 rounded-xl shadow-lg z-50"
      >
        {{ toast.message }}
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useAppStore } from '../stores/useAppStore'
import { useAuthStore } from '../stores/useAuthStore'
import { useNotificationStore } from '../stores/useNotificationStore'
import ConfirmDialog from '../components/ConfirmDialog.vue'
import CreateAccountDialog from '../components/CreateAccountDialog.vue'
import LoginDialog from '../components/LoginDialog.vue'
import ReauthenticateDialog from '../components/ReauthenticateDialog.vue'
import NotificationGuideDialog from '../components/NotificationGuideDialog.vue'
import type { Baby, NotificationTiming } from '../types'
import {
  getNotificationPermission,
  rescheduleAllNotifications,
  getFCMToken
} from '../services/notificationService'
import { getVaccineDetails, getDoseDetails } from '../services/scheduleService'
import {
  generateFamilyCode,
  createFamilyCode,
  joinFamily,
  approveMember,
  rejectMember,
  cancelJoinRequest,
  leaveFamily,
  revokeAccess,
  updateMemberNickname,
  updateFamilyName,
} from '../services/familyService'
import { deleteDocument, setDocument } from '../services/firestoreService'
import { Capacitor } from '@capacitor/core'
import { App } from '@capacitor/app'
import { getFormattedVersion } from '../utils/version'

const isIOS = Capacitor.getPlatform() === 'ios'
const isAndroid = Capacitor.getPlatform() === 'android'

// ===== Pull-to-refresh =====
const PULL_THRESHOLD = 60
const pullStartY = ref(0)
const pullDistance = ref(0)
const isPulling = ref(false)
const isRefreshing = ref(false)

const pullIndicatorHeight = computed(() =>
  isRefreshing.value ? 48 : pullDistance.value
)

const onPullStart = (e: TouchEvent) => {
  if (window.scrollY === 0 && e.touches[0]) {
    pullStartY.value = e.touches[0].clientY
    isPulling.value = true
  }
}

const onPullMove = (e: TouchEvent) => {
  if (!isPulling.value || !e.touches[0]) return
  const delta = e.touches[0].clientY - pullStartY.value
  pullDistance.value = delta > 0 ? Math.min(delta * 0.5, PULL_THRESHOLD * 1.5) : 0
}

const onPullEnd = async () => {
  if (!isPulling.value) return
  isPulling.value = false
  if (pullDistance.value >= PULL_THRESHOLD) {
    pullDistance.value = PULL_THRESHOLD
    isRefreshing.value = true
    try {
      await appStore.initFromCloud()
    } finally {
      isRefreshing.value = false
      pullDistance.value = 0
    }
  } else {
    pullDistance.value = 0
  }
}

// 行事曆偏好（iOS only）
function readCalendarPreference(): 'google' | 'apple' | null {
  const v = localStorage.getItem('calendarPreference')
  return v === 'google' || v === 'apple' ? v : null
}
const calendarPreference = ref<'google' | 'apple' | null>(readCalendarPreference())

const setCalendarPreference = (provider: 'google' | 'apple') => {
  localStorage.setItem('calendarPreference', provider)
  calendarPreference.value = provider
}

const router = useRouter()
const appStore = useAppStore()
const authStore = useAuthStore()
const notificationStore = useNotificationStore()

// 應用程式版本號（從原生平台讀取）
const appVersion = ref('載入中...')

// ===== 家庭共享狀態 =====

const joinCodeInput = ref('')
const familyActionLoading = ref(false)
const familyError = ref('')
const showLeaveFamilyConfirm = ref(false)
const showDeleteFamilyCodeConfirm = ref(false)

// 家庭名稱（建立時輸入）
const newFamilyName = ref('')
// 家庭名稱（Owner 編輯）
const editFamilyNameValue = ref('')
const editingFamilyName = ref(false)

const handleUpdateFamilyName = async () => {
  if (!authStore.uid) return
  editingFamilyName.value = false
  try {
    await updateFamilyName(authStore.uid, editFamilyNameValue.value)
    authStore.familyName = editFamilyNameValue.value.trim() || null
    showToast('已更新家庭名稱')
  } catch {
    showToast('更新失敗，請稍後再試')
  }
}

const startEditFamilyName = () => {
  editFamilyNameValue.value = authStore.familyName ?? ''
  editingFamilyName.value = true
}

const familyMembers = computed(() => Object.entries(authStore.sharedWith))
const pendingMembersList = computed(() => Object.entries(authStore.pendingMembers))

const showCancelRequestConfirm = ref(false)

const handleApproveMember = async (memberUid: string) => {
  familyActionLoading.value = true
  try {
    await approveMember(memberUid)
    const pending = authStore.pendingMembers[memberUid]
    const memberCount = Object.keys(authStore.sharedWith).length
    const newSharedWith = {
      ...authStore.sharedWith,
      [memberUid]: { email: pending?.email, nickname: `成員${memberCount + 1}`, joinedAt: new Date().toISOString() },
    }
    const newPending = { ...authStore.pendingMembers }
    delete newPending[memberUid]
    authStore.sharedWith = newSharedWith
    authStore.pendingMembers = newPending
    showToast('已同意加入')
  } catch {
    showToast('操作失敗，請稍後再試')
  } finally {
    familyActionLoading.value = false
  }
}

const handleRejectMember = async (memberUid: string) => {
  familyActionLoading.value = true
  try {
    await rejectMember(memberUid)
    const newPending = { ...authStore.pendingMembers }
    delete newPending[memberUid]
    authStore.pendingMembers = newPending
    showToast('已拒絕申請')
  } catch {
    showToast('操作失敗，請稍後再試')
  } finally {
    familyActionLoading.value = false
  }
}

const handleCancelJoinRequest = async () => {
  showCancelRequestConfirm.value = false
  familyActionLoading.value = true
  try {
    await cancelJoinRequest()
    authStore.pendingWith = null
    showToast('已取消申請')
  } catch {
    showToast('取消失敗，請稍後再試')
  } finally {
    familyActionLoading.value = false
  }
}

const revokeDialog = reactive({
  show: false,
  memberUid: '',
  nickname: '',
})

const editNicknameDialog = reactive({
  show: false,
  memberUid: '',
  value: '',
})

const copyFamilyCode = async () => {
  if (!authStore.familyCode) return
  try {
    await navigator.clipboard.writeText(authStore.familyCode)
    showToast('已複製家庭代碼')
  } catch {
    showToast('請手動複製代碼：' + authStore.familyCode)
  }
}

const handleCreateFamilyCode = async () => {
  if (!authStore.uid) return
  familyActionLoading.value = true
  try {
    const code = generateFamilyCode()
    await createFamilyCode(authStore.uid, code, newFamilyName.value)
    authStore.familyCode = code
    authStore.familyName = newFamilyName.value.trim() || null
    newFamilyName.value = ''
    showToast('家庭代碼已建立')
  } catch (err: any) {
    showToast('建立失敗，請稍後再試')
    console.error('[Settings] createFamilyCode 失敗:', err)
  } finally {
    familyActionLoading.value = false
  }
}

const regenerateConfirmDialog = ref(false)

const handleRegenerateCode = () => {
  if (!authStore.uid) return
  if (familyMembers.value.length > 0) {
    regenerateConfirmDialog.value = true
    return
  }
  doRegenerateCode()
}

const doRegenerateCode = async () => {
  regenerateConfirmDialog.value = false
  if (!authStore.uid) return
  familyActionLoading.value = true
  try {
    const code = generateFamilyCode()
    await createFamilyCode(authStore.uid, code)
    authStore.familyCode = code
    showToast('代碼已更新')
  } catch {
    showToast('更新失敗，請稍後再試')
  } finally {
    familyActionLoading.value = false
  }
}

const handleDeleteFamilyCode = async () => {
  if (!authStore.uid || !authStore.familyCode) return
  showDeleteFamilyCodeConfirm.value = false
  familyActionLoading.value = true
  try {
    // 拒絕所有待審核申請
    for (const memberUid of Object.keys(authStore.pendingMembers)) {
      try { await rejectMember(memberUid) } catch { /* 忽略 */ }
    }
    // 透過 Cloud Function 移除所有成員（同時清除成員的 linkedTo）
    for (const memberUid of Object.keys(authStore.sharedWith)) {
      try { await revokeAccess(memberUid) } catch { /* 忽略 */ }
    }
    // 刪除 familyCodes 查找表
    await deleteDocument(`familyCodes/${authStore.familyCode}`)
    // 清除自己的 familyCode（sharedWith 已由 revokeAccess 清空）
    await setDocument(`users/${authStore.uid}`, {
      familyCode: null,
      familyName: authStore.familyName ?? null,
      sharedWith: {},
      pendingMembers: {},
      linkedTo: authStore.linkedTo ?? null,
    })
    authStore.familyCode = null
    authStore.sharedWith = {}
    showToast('已解除家庭共享')
  } catch {
    showToast('解除失敗，請稍後再試')
  } finally {
    familyActionLoading.value = false
  }
}

const handleJoinFamily = async () => {
  familyError.value = ''
  if (joinCodeInput.value.length < 6) return
  familyActionLoading.value = true
  console.log('[joinFamily] calling CF with code:', joinCodeInput.value.trim())
  try {
    const { ownerUid } = await joinFamily(joinCodeInput.value.trim())
    console.log('[joinFamily] success, ownerUid:', ownerUid)
    authStore.pendingWith = { uid: ownerUid, familyCode: joinCodeInput.value.trim().toUpperCase() }
    joinCodeInput.value = ''
    showToast('已送出申請，等待對方審核')
  } catch (err: any) {
    console.error('[joinFamily] error:', err?.code, err?.message, err)
    const msg: string = err?.message ?? ''
    if (err?.code === 'functions/not-found' || msg.includes('找不到此家庭代碼')) {
      familyError.value = '找不到此家庭代碼，請確認後再試'
    } else if (err?.code === 'functions/already-exists' || msg.includes('已加入家庭')) {
      familyError.value = '您已加入家庭，請先離開再加入新家庭'
    } else if (err?.code === 'functions/failed-precondition' || msg.includes('無法加入自己')) {
      familyError.value = '無法加入自己的家庭代碼'
    } else if (err?.code === 'functions/permission-denied' || msg.includes('並未加入')) {
      familyError.value = '權限不足，請重新登入後再試'
    } else if (msg) {
      familyError.value = msg
    } else {
      familyError.value = `加入失敗，請稍後再試`
    }
  } finally {
    familyActionLoading.value = false
  }
}

const handleLeaveFamily = async () => {
  if (!authStore.linkedTo) return
  showLeaveFamilyConfirm.value = false
  familyActionLoading.value = true
  try {
    await leaveFamily(authStore.linkedTo.uid)
    authStore.linkedTo = null
    showToast('已離開家庭')
    // 重新載入自己的資料
    await appStore.initFromCloud()
  } catch (err: any) {
    showToast('離開失敗，請稍後再試')
    console.error('[Settings] leaveFamily 失敗:', err)
  } finally {
    familyActionLoading.value = false
  }
}

const showRevokeConfirm = (memberUid: string, nickname: string) => {
  revokeDialog.memberUid = memberUid
  revokeDialog.nickname = nickname
  revokeDialog.show = true
}

const handleRevokeAccess = async () => {
  revokeDialog.show = false
  familyActionLoading.value = true
  try {
    await revokeAccess(revokeDialog.memberUid)
    // 移除本地 state 中的成員
    const updated = { ...authStore.sharedWith }
    delete updated[revokeDialog.memberUid]
    authStore.sharedWith = updated
    showToast('已移除成員')
  } catch (err: any) {
    showToast('移除失敗，請稍後再試')
    console.error('[Settings] revokeAccess 失敗:', err)
  } finally {
    familyActionLoading.value = false
  }
}

const openEditNickname = (memberUid: string, currentNickname: string) => {
  editNicknameDialog.memberUid = memberUid
  editNicknameDialog.value = currentNickname
  editNicknameDialog.show = true
}

const handleUpdateNickname = async () => {
  if (!authStore.uid || !editNicknameDialog.value.trim()) return
  editNicknameDialog.show = false
  try {
    await updateMemberNickname(authStore.uid, editNicknameDialog.memberUid, editNicknameDialog.value.trim())
    // 更新本地 state
    const existing = authStore.sharedWith[editNicknameDialog.memberUid]
    if (existing) {
      authStore.sharedWith = {
        ...authStore.sharedWith,
        [editNicknameDialog.memberUid]: {
          email: existing.email,
          joinedAt: existing.joinedAt,
          nickname: editNicknameDialog.value.trim(),
        },
      }
    }
    showToast('已更新名稱')
  } catch (err: any) {
    showToast('更新失敗，請稍後再試')
    console.error('[Settings] updateMemberNickname 失敗:', err)
  }
}

const createAccountDialogRef = ref<InstanceType<typeof CreateAccountDialog> | null>(null)
const loginDialogRef = ref<InstanceType<typeof LoginDialog> | null>(null)
const reauthenticateDialogRef = ref<InstanceType<typeof ReauthenticateDialog> | null>(null)

// 推播設定
const TIMING_OPTIONS = [
  { value: '7days' as NotificationTiming, label: '7 天前', description: '早上 9:00 提醒' },
  { value: '1day' as NotificationTiming, label: '1 天前', description: '早上 9:00 提醒' },
  { value: 'morning' as NotificationTiming, label: '當天早上', description: '早上 8:00 提醒' },
]

const selectedTimings = ref<NotificationTiming[]>([])

// 控制提醒時間點選項的展開/收起（純 UI 狀態，不持久化）
const showTimingOptions = ref(false)

// 初始化推播設定
onMounted(async () => {
  // 確保 store 已載入設定（如果尚未載入，等待載入）
  if (!notificationStore.loaded) {
    await notificationStore.loadSettings()
  }

  // 同步 UI 狀態
  selectedTimings.value = [...notificationStore.settings.timings]

  // 更新權限狀態（可能在背景被使用者修改）
  const permission = await getNotificationPermission()
  notificationStore.updatePermission(permission)

  // 載入應用程式版本號
  appVersion.value = await getFormattedVersion()
})

// 刪除寶寶對話框狀態
const deleteBabyDialog = reactive({
  show: false,
  title: '',
  message: '',
  targetBaby: null as Baby | null
})

// 刪除帳號對話框狀態
const deleteAccountDialog = reactive({
  show: false,
  loading: false
})

// 重新驗證對話框狀態
const reauthenticateDialog = reactive({
  show: false
})

// 建立帳號對話框狀態
const createAccountDialog = reactive({
  show: false
})

// 登入對話框狀態
const loginDialog = reactive({
  show: false
})

// 通知引導對話框狀態
const notificationGuideDialog = reactive({
  show: false
})

// Toast 提示
const toast = reactive({
  show: false,
  message: ''
})

const showToast = (message: string) => {
  toast.message = message
  toast.show = true
  setTimeout(() => {
    toast.show = false
  }, 2000)
}

// 顯示刪除寶寶對話框
const showDeleteBabyDialog = (baby: Baby) => {
  deleteBabyDialog.title = '刪除寶寶資料'
  deleteBabyDialog.message = `確定要刪除「${baby.nickname}」的所有資料嗎？\n\n包含接種紀錄與提醒，此動作無法復原。`
  deleteBabyDialog.targetBaby = baby
  deleteBabyDialog.show = true
}

// 處理刪除寶寶
const handleDeleteBaby = async () => {
  if (!deleteBabyDialog.targetBaby) return

  try {
    await appStore.deleteBaby(deleteBabyDialog.targetBaby.id)
    closeBabyDialog()
    showToast('已成功刪除')

    // 如果沒有寶寶了，跳轉到首頁
    if (appStore.babies.length === 0) {
      router.push('/')
    }
  } catch (err) {
    closeBabyDialog()
    showToast('刪除失敗，請稍後再試')
    console.error(err)
  }
}

// 關閉刪除寶寶對話框
const closeBabyDialog = () => {
  deleteBabyDialog.show = false
  deleteBabyDialog.targetBaby = null
}

// 顯示刪除帳號對話框
const showDeleteAccountDialog = () => {
  deleteAccountDialog.show = true
}

// 處理刪除帳號確認（第一步）
const handleDeleteAccount = async () => {
  // 如果是 Email 帳號，一律需要重新驗證（安全考量）
  if (!authStore.isAnonymous) {
    closeDeleteAccountDialog()
    reauthenticateDialog.show = true
  } else {
    // 匿名帳號直接刪除，顯示 loading 狀態
    deleteAccountDialog.loading = true
    await performDeleteAccount()
  }
}

// 處理重新驗證（第二步，僅 Email 帳號）
const handleReauthenticate = async (password: string) => {
  await performDeleteAccount(password)
}

// 執行實際的刪除操作
const performDeleteAccount = async (password?: string) => {
  try {
    // 刪除前清理家庭關係（Auth 仍有效，Firestore 寫入才會成功）
    if (authStore.pendingWith) {
      // Guest 申請中：先取消申請
      try { await cancelJoinRequest() } catch { /* 忽略，繼續刪除 */ }
    } else if (authStore.linkedTo) {
      // Guest：先離開家庭，避免 owner 的 sharedWith 留下 ghost member
      try { await leaveFamily(authStore.linkedTo.uid) } catch { /* 忽略，繼續刪除 */ }
    } else if (authStore.familyCode && authStore.uid) {
      // Owner：透過 Cloud Function 移除所有成員（同時清除成員的 linkedTo），並刪除 familyCodes 查找表
      for (const memberUid of Object.keys(authStore.sharedWith)) {
        try { await revokeAccess(memberUid) } catch { /* 忽略 */ }
      }
      try { await deleteDocument(`familyCodes/${authStore.familyCode}`) } catch { /* 忽略 */ }
    }

    // 刪除所有 Firestore 資料
    await appStore.resetAllData()

    // 再刪除 Firebase Auth 帳號
    await authStore.deleteAccount(password)

    closeReauthenticateDialog()
    showToast('帳號已刪除，即將重新載入頁面')
    setTimeout(() => {
      location.reload()
    }, 1000)
  } catch (err: any) {
    console.error('刪除帳號失敗:', {
      error: err,
      message: err?.message,
      code: err?.code
    })

    if (reauthenticateDialog.show) {
      // 如果是在重新驗證對話框中出錯
      reauthenticateDialogRef.value?.setError(err.message || '刪除失敗，請稍後再試')
      reauthenticateDialogRef.value?.setLoading(false)
    } else {
      // 匿名帳號刪除出錯
      showToast('刪除失敗，請稍後再試')
    }
  }
}

// 關閉刪除帳號對話框
const closeDeleteAccountDialog = () => {
  deleteAccountDialog.show = false
}

// 關閉重新驗證對話框
const closeReauthenticateDialog = () => {
  reauthenticateDialog.show = false
  reauthenticateDialogRef.value?.resetForm()
}

// 顯示建立帳號對話框
const showCreateAccountDialog = () => {
  createAccountDialog.show = true
}

// 處理建立帳號（升級匿名帳號為正式帳號）
const handleCreateAccount = async (email: string, password: string) => {
  try {
    // 如果當前是匿名帳號，使用 linkWithEmail 升級
    // 如果不是匿名帳號，會拋出錯誤
    await authStore.linkWithEmail(email, password)
    createAccountDialogRef.value?.resetForm()
    closeCreateAccountDialog()
    showToast('帳號建立成功！')
  } catch (error: any) {
    createAccountDialogRef.value?.setError(error.message || '建立失敗，請稍後再試')
    createAccountDialogRef.value?.setLoading(false)
  }
}

// 關閉建立帳號對話框
const closeCreateAccountDialog = () => {
  createAccountDialog.show = false
}

// 顯示登入對話框
const showLoginDialog = () => {
  loginDialog.show = true
}

// 處理登入（保護匿名資料，登入失敗不刪除）
const handleLogin = async (email: string, password: string) => {
  try {
    // 先登入（不會刪除當前的匿名帳號，Firebase 會自動切換用戶）
    await authStore.signInWithEmail(email, password)

    // 登入成功後，嘗試載入資料
    try {
      await appStore.initFromCloud()
    } catch (dataError) {
      console.warn('載入資料失敗，可能是新帳號或權限問題:', dataError)
    }

    loginDialogRef.value?.resetForm()
    closeLoginDialog()
    showToast('登入成功！')

    // 登入成功後導向 Timeline
    setTimeout(() => {
      router.push('/timeline')
    }, 500)
  } catch (error: any) {
    // 登入失敗，保持當前的匿名帳號狀態，不做任何更改
    loginDialogRef.value?.setError(error.message || '登入失敗，請稍後再試')
    loginDialogRef.value?.setLoading(false)
  }
}

// 關閉登入對話框
const closeLoginDialog = () => {
  loginDialog.show = false
}

// ===== 推播相關處理函數 =====

// ===== 推播相關處理函數 =====

// 切換推播開關（現在只控制展開/收起）
const toggleNotifications = async () => {
  // 檢查權限狀態
  const currentPermission = await getNotificationPermission()

  if (currentPermission !== 'granted') {
    // 沒有權限
    if (currentPermission === 'prompt') {
      // 首次請求，顯示引導對話框
      notificationGuideDialog.show = true
    } else {
      // 權限被拒絕，提示需要去設定開啟
      showToast('需要開啟通知權限才能使用推播功能')
      await openAppSettings()
    }
    return
  }

  // 有權限，切換展開/收起
  showTimingOptions.value = !showTimingOptions.value
}

// 處理通知引導對話框的確認
const handleNotificationGuideConfirm = async () => {
  notificationGuideDialog.show = false

  // 請求權限
  const { requestNotificationPermission } = await import('../services/notificationService')
  const newPermission = await requestNotificationPermission()

  if (newPermission === 'granted') {
    // 權限獲得，儲存 FCM Token
    const token = await getFCMToken()
    if (token) {
      await notificationStore.saveFCMToken(token)
    }

    // 更新權限狀態
    notificationStore.updatePermission(newPermission)

    // 展開選項（預設全部勾選已經在 onMounted 載入）
    showTimingOptions.value = true

    // 排程推播
    await scheduleNotifications()
    showToast('已開啟推播提醒')
  } else {
    // 權限被拒絕，引導到系統設定
    showToast('需要允許通知權限才能使用推播功能')
    await openAppSettings()
  }
}

// 處理通知引導對話框的取消
const handleNotificationGuideCancel = () => {
  notificationGuideDialog.show = false
}

// 開啟系統設定中的應用程式通知設定
const openAppSettings = async () => {
  if (Capacitor.isNativePlatform()) {
    try {
      const platform = Capacitor.getPlatform()

      if (platform === 'ios') {
        // iOS: 使用 app-settings: URL scheme 打開 App 設定頁面
        window.open('app-settings:', '_system')
      } else if (platform === 'android') {
        // Android: 使用 intent URL scheme 打開 App 詳細資訊頁面
        const appInfo = await App.getInfo()
        window.open(`package:${appInfo.id}`, '_system')
      }
    } catch (error) {
      showToast('無法開啟設定，請手動到「設定」中開啟通知權限')
    }
  } else {
    showToast('請到瀏覽器設定中允許通知權限')
  }
}

// 更新提醒時間點（勾選變更時自動儲存和重新排程）
const updateTimings = async () => {
  try {
    // 1. 先儲存到 Firestore（確保持久化）
    await notificationStore.updateTimings(selectedTimings.value)

    // 2. 然後重新排程推播
    await scheduleNotificationsWithTimings(selectedTimings.value)

    // 3. 顯示提示
    if (selectedTimings.value.length === 0) {
      showToast('已關閉所有推播提醒')
    } else {
      showToast('已更新提醒時間點')
    }
  } catch (error) {
    showToast('更新失敗，請重試')
  }
}

// 使用指定的時間點排程推播（不依賴 store）
const scheduleNotificationsWithTimings = async (timings: NotificationTiming[]) => {
  // 檢查權限
  if (!notificationStore.hasPermission) {
    return
  }

  // 檢查是否有選擇任何提醒時間點
  if (timings.length === 0) {
    const { cancelAllNotifications } = await import('../services/notificationService')
    await cancelAllNotifications()
    return
  }

  const records = appStore.allBabiesRecords

  await rescheduleAllNotifications(
    records,
    (babyId) => {
      const baby = appStore.babies.find(b => b.id === babyId)
      return baby?.nickname || '寶寶'
    },
    (vaccineId) => getVaccineDetails(vaccineId)?.name || '',
    (doseId) => getDoseDetails(doseId)?.label || '',
    timings
  )
}

// 排程所有推播
const scheduleNotifications = async () => {
  // 檢查權限
  if (!notificationStore.hasPermission) {
    return
  }

  // 檢查是否有選擇任何提醒時間點
  if (selectedTimings.value.length === 0) {
    // 取消所有推播
    const { cancelAllNotifications } = await import('../services/notificationService')
    await cancelAllNotifications()
    return
  }

  // 使用所有寶寶的記錄，而非只有當前寶寶
  const records = appStore.allBabiesRecords

  await rescheduleAllNotifications(
    records,
    (babyId) => {
      const baby = appStore.babies.find(b => b.id === babyId)
      return baby?.nickname || '寶寶'
    },
    (vaccineId) => getVaccineDetails(vaccineId)?.name || '',
    (doseId) => getDoseDetails(doseId)?.label || '',
    selectedTimings.value
  )
}

</script>

<style scoped>
.toast-enter-active,
.toast-leave-active {
  transition: all 0.3s ease;
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(10px);
}
</style>
